import { useRef } from 'react';
import { useAppDispatch } from '../../../store/store';
import type { AppDispatch } from '../../../store/store';
import {
  getDispensarizationSettings,
  getFormSettings,
  getNotificationSettings,
  getSlotSettings,
} from '../../../store/reducers/ClinicSettingsDetailSlice';
import type { NotificationSettings } from '../../../models/Settings/NotificationSettings';
import { normalizeCallsDisable, timespanToMinutes } from './callSettings';
import type { ScenarioType, ScenarioValues } from './callSettings';

const SCENARIO_DEFAULTS = {
  slot: { callsMaxTries: 0, callsIntervalMinutes: 0 },
  notification: { callsMaxTries: 3, callsIntervalMinutes: 60 },
  dispensarization: { callsMaxTries: 3, callsIntervalMinutes: 60 },
  form: { callsMaxTries: 3, callsIntervalMinutes: 60 },
} as const;

export const getScenarioValues = (
  type: ScenarioType,
  data: Pick<
    NotificationSettings,
    'callsMaxTries' | 'callsInterval' | 'callsDisable'
  >,
): ScenarioValues => ({
  callsMaxTries: data.callsMaxTries ?? SCENARIO_DEFAULTS[type].callsMaxTries,
  callsIntervalMinutes: data.callsInterval
    ? timespanToMinutes(data.callsInterval)
    : SCENARIO_DEFAULTS[type].callsIntervalMinutes,
  callsDisable: normalizeCallsDisable(data.callsDisable),
});

export const loadScenarioSettings = (
  dispatch: AppDispatch,
  clinicId: string,
  type: ScenarioType,
) => {
  switch (type) {
    case 'slot':
      return dispatch(getSlotSettings(clinicId)).unwrap();
    case 'notification':
      return dispatch(getNotificationSettings(clinicId)).unwrap();
    case 'dispensarization':
      return dispatch(getDispensarizationSettings(clinicId)).unwrap();
    case 'form':
      return dispatch(getFormSettings(clinicId)).unwrap();
    default: {
      const unsupported: never = type;
      throw new RangeError(`Unsupported scenario: ${unsupported}`);
    }
  }
};

export const useScenarioSettingsRequests = (clinicId: string | undefined) => {
  const dispatch = useAppDispatch();
  const activeRequestsRef = useRef({
    clinicId,
    versions: new Map<ScenarioType, number>(),
  });
  if (activeRequestsRef.current.clinicId !== clinicId) {
    activeRequestsRef.current = {
      clinicId,
      versions: new Map<ScenarioType, number>(),
    };
  }

  const runRequest = <T>(
    type: ScenarioType,
    request: (id: string) => Promise<T>,
  ): Promise<T | undefined> => {
    const owner = activeRequestsRef.current;
    if (!owner.clinicId) return Promise.resolve(undefined);
    const version = (owner.versions.get(type) ?? 0) + 1;
    owner.versions.set(type, version);
    const isCurrent = () =>
      activeRequestsRef.current === owner &&
      owner.versions.get(type) === version;

    return request(owner.clinicId).then(
      (data) => (isCurrent() ? data : undefined),
      (error: unknown) => {
        if (!isCurrent()) return undefined;
        throw error;
      },
    );
  };

  return {
    loadSlotSettings: () =>
      runRequest('slot', (id) => dispatch(getSlotSettings(id)).unwrap()),
    loadScenarioSettings: (type: ScenarioType) =>
      runRequest(type, (id) => loadScenarioSettings(dispatch, id, type)),
  };
};
